/** Default Soul Blueprint Revelation content — editable in Admin → Website → Homepage → Blueprint Immersion */

export const DEFAULT_HEADLINE_OPTIONS = [
  'Why do you keep repeating the same pain? Your past life holds the answer.',
  'Stop guessing. Your soul\'s blueprint is finally ready to be read.',
  'The stuckness you feel is not a flaw — it\'s a memory.',
];

export const DEFAULT_INTRO_BODY = `You've tried journaling. Therapy. Meditation. Manifestation.

The same fears. The same relationship patterns. The same invisible ceiling in your career or healing gifts.

That is not a coincidence. That is your soul remembering.

Every lifetime leaves an echo. A wound. An oath. A gift you forgot you carried.

Soul Blueprint Revelation is not a generic personality test. It is a channelized psychic reading using your exact birth data — date, time, and place of birth — to unlock five sacred keys.`;

export const DEFAULT_SACRED_KEYS = [
  {
    id: 'key_signature',
    emoji: '🔥',
    title: 'Energetic Soul Signature',
    tagline: 'The archetype your soul chose before you were born',
    body: `Before you had a name. Before you had wounds. Before you learned to shrink or perform or survive — your soul chose a frequency. A specific lens through which you would experience everything: love, loss, fear, joy, purpose, and pain.

Most people spend decades fighting their own signature — exhausting themselves trying to be what they are not.

In your reading, I will reveal your unique Soul Signature. Not as a label — as a remembering.

You will finally understand:
- Why certain doors always close
- Why certain people feel like home
- What you came here to master
- What you are finally allowed to release

You are not broken. You have just been living against your own design. That ends now.`,
    visible: true,
  },
  {
    id: 'key_past_life',
    emoji: '🌙',
    title: 'Past Life Revelation',
    tagline: 'The lifetime still breathing inside you',
    body: `You have lived before. Not in metaphor. Not in fantasy. In flesh. In bone. In joy. In terror.

And that life did not end cleanly.

A wound was left open. An oath was sworn in desperation. A truth was buried before it could be spoken. And that unfinished business? It followed you here.

In your Past Life Revelation, I will show you:
- Who you were (The Silent Healer. The Exiled Queen. The Burnt Mystic. The Forgotten Father)
- Where you lived — the land, the era, the energy
- The wound you carried — the one that never fully healed
- The oath you swore — the one still binding your choices today
- The karmic lesson — what your soul came here to finally complete

This is not imagination. This is soul memory. And once you see it — you can finally heal it.`,
    visible: true,
  },
  {
    id: 'key_timeline',
    emoji: '🧭',
    title: 'Current Timeline Solution',
    tagline: 'Direction-oriented guidance for where you are right now',
    body: `You already know what's wrong. You feel it every morning. The heaviness. The loop. The same argument. The same ceiling.

What you need is not more analysis. You need direction.

The Current Timeline Solution is not vague spiritual advice. It is a soul-level compass for the next 30 days:
- A specific action to take — or bravely release
- A ritual tailored to your past life wound
- A boundary to draw — with a person, a habit, or yourself
- A question to sit with each morning
- A warning from your guides about what is no longer yours to carry

This is practical. This is grounded. And it is always, always oriented toward one thing: your forward movement.

You are not here to stay stuck. You are here to remember how to walk again.`,
    visible: true,
  },
  {
    id: 'key_guidance',
    emoji: '🕯️',
    title: 'Soul Guidance',
    tagline: 'The North Star for this incarnation',
    body: `In every reading, something sacred happens.

A single sentence arrives. Sometimes one word. It is not from me. It is from you — the version of you who already completed the healing.

This is your Soul Guidance:
- A mantra for the hard mornings
- A warning you will wish you heard sooner
- A permission slip you have been waiting your whole life to receive
- A truth your soul has been hiding from your mind

You will return to this sentence again and again. Before big decisions. In moments of doubt. When you forget who you are.

Most clients tattoo this somewhere — on paper, on a mirror, or in their heart.`,
    visible: true,
  },
  {
    id: 'key_echo',
    emoji: '⚡',
    title: 'The Soul Echo Transmission',
    tagline: 'The unfinished message from your past self — channeled, not read',
    body: `This is the part no one else offers.

Because no algorithm can do this. No chart can predict this. No template can fake this.

In every past life, something was left unsaid:
- A truth you died before speaking
- A warning you never received
- A love you never confessed
- A forgiveness you never granted yourself

That unfinished sentence has been following you across lifetimes — screaming silently beneath your anxiety, your exhaustion, your fear of being seen.

In the Soul Echo Transmission, I step aside and let your past self speak directly to you.

Not through me. Not interpreted by me. Channeled. Word for word. As if they are standing in the room.

You will feel this in your body when you read it. The chills. The tears. The exhale you didn't know you were holding.

That is the sound of a soul finally being heard across time.`,
    visible: true,
  },
];

export const DEFAULT_BLUEPRINT_SERVICES = [
  {
    id: 'tier_whisper',
    badge: '✨',
    title: 'Whisper Reading',
    description: '7–10 page PDF reading delivered within 48 hours. All five sacred keys included.',
    price_label: '$33',
    features: ['7–10 page PDF reading', 'Delivered within 48 hours', 'All five sacred keys included'],
    category_label: '1:1 Session',
    cta_label: 'Know More',
    enroll_label: 'Book Now',
    visible: true,
  },
  {
    id: 'tier_voice',
    badge: '🔥',
    title: 'Voice of Spirit',
    description: 'Everything in Whisper Reading plus a 20-minute personal channeled audio interpretation.',
    price_label: '$77',
    features: ['Everything in Whisper Reading', 'PLUS 20 minute personal channeled audio interpretation'],
    category_label: '1:1 Session',
    cta_label: 'Know More',
    enroll_label: 'Book Now',
    visible: true,
  },
  {
    id: 'tier_portal',
    badge: '🕯️',
    title: 'Portal Session',
    description: 'Everything in Voice of Spirit plus a 45-minute live Zoom session (recorded).',
    price_label: '$144',
    features: ['Everything in Voice of Spirit', 'PLUS 45 minute live Zoom session (recorded)'],
    category_label: '1:1 Session',
    cta_label: 'Know More',
    enroll_label: 'Book Now',
    visible: true,
  },
  {
    id: 'tier_ancestral',
    badge: '🌙',
    title: 'Ancestral Healing Circle',
    description: 'Three 60-minute sessions over 3 months with updated readings and ritual assignments.',
    price_label: '$333',
    features: ['Three 60-minute sessions over 3 months', 'Updated readings each time', 'Ritual assignments between sessions'],
    category_label: '1:1 Session',
    cta_label: 'Know More',
    enroll_label: 'Book Now',
    visible: true,
  },
];

export const DEFAULT_BLUEPRINT_TESTIMONIALS = [
  {
    id: 't1',
    quote: "I cried reading my past life. She described fears I've never told anyone. This wasn't a report — it was a homecoming.",
    author: 'Jessica R.',
    visible: true,
  },
  {
    id: 't2',
    quote: 'The solution she gave me was so simple I almost ignored it. But I followed it. Within two weeks, my chronic anxiety dropped by half.',
    author: 'David L.',
    visible: true,
  },
  {
    id: 't3',
    quote: 'The soul guidance still sits on my mirror. I read it every morning. It changed how I move through the world.',
    author: 'Amina K.',
    visible: true,
  },
  {
    id: 't4',
    quote: "The Soul Echo Transmission made me sob. She wrote exactly what my past self has been screaming at me for years. I finally feel seen.",
    author: 'Marcus T.',
    visible: true,
  },
];

export const DEFAULT_DISCLAIMER =
  'This reading is channeled spiritual guidance — not medical, psychological, or financial advice. You are always your own ultimate authority. Past life revelations are energetically received and symbolic, offered to support your healing journey. The Soul Echo Transmission is channeled live for each client. It is never pre-written, never repeated, and never generated by AI.';

export const DEFAULT_BLUEPRINT_SECTION = {
  id: 'blueprint_immersion',
  title: 'The Divine Iris Blueprint Immersion',
  subtitle: "Unfolding Your Soul's Path to Ultimate Alignment & Healing",
  kicker: 'Exclusive Personalized Sessions',
  component: 'BlueprintImmersionSection',
  removable: false,
  service_category_label: '1:1 Session',
  package_title: 'Soul Blueprint Revelation',
  package_subtitle: 'A Channeled Psychic Reading Across Lifetimes',
  headline: DEFAULT_HEADLINE_OPTIONS[1],
  intro_body: DEFAULT_INTRO_BODY,
  sacred_keys_heading: 'The Five Sacred Keys',
  sacred_keys: DEFAULT_SACRED_KEYS,
  services: DEFAULT_BLUEPRINT_SERVICES,
  testimonials_heading: 'What Clients Say',
  testimonials: DEFAULT_BLUEPRINT_TESTIMONIALS,
  disclaimer: DEFAULT_DISCLAIMER,
  cta_text: 'Book Your Soul Blueprint Revelation Now',
  cta_link: '',
  show_sacred_keys: true,
  show_testimonials: true,
  show_disclaimer: true,
};

/** Merge saved section with defaults for any missing fields */
export function mergeBlueprintSection(section = {}) {
  const services =
    section.services && section.services.length > 0 ? section.services : DEFAULT_BLUEPRINT_SERVICES;
  const sacred_keys =
    section.sacred_keys && section.sacred_keys.length > 0 ? section.sacred_keys : DEFAULT_SACRED_KEYS;
  const testimonials =
    section.testimonials && section.testimonials.length > 0
      ? section.testimonials
      : DEFAULT_BLUEPRINT_TESTIMONIALS;

  return {
    ...DEFAULT_BLUEPRINT_SECTION,
    ...section,
    services,
    sacred_keys,
    testimonials,
    intro_body: section.intro_body || DEFAULT_INTRO_BODY,
    headline: section.headline || DEFAULT_HEADLINE_OPTIONS[1],
    disclaimer: section.disclaimer ?? DEFAULT_DISCLAIMER,
    cta_text: section.cta_text || 'Book Your Soul Blueprint Revelation Now',
  };
}
